// src/hooks/useMediaQuery.ts

import { useState, useEffect } from "react";

/**
 * Custom hook to track a CSS media query state.
 * This is useful for building responsive UIs directly in React components.
 * * @param query The media query string (e.g., '(max-width: 768px)').
 * @returns A boolean indicating if the media query currently matches.
 */
export const useMediaQuery = (query: string): boolean => {
  // Initialize state based on the current window size (default to false if window is undefined, e.g., during SSR)
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    // Prevent execution if running in a server-side rendering environment
    if (typeof window === "undefined") return;

    const media = window.matchMedia(query);

    // Define the listener function
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Set initial value immediately when the component mounts
    setMatches(media.matches);

    // Use addEventListener for modern browsers
    // Note: Older browsers might require media.addListener(listener)
    media.addEventListener("change", listener);

    // Cleanup function to remove the listener when the component unmounts
    return () => {
      media.removeEventListener("change", listener);
    };
  }, [query]); // Re-run effect only if the query string changes

  return matches;
};